export interface KeyedListMutationOperations<TCreate, TUpdate> {
  creates: TCreate[];
  updates: TUpdate[];
  removeIDs: string[];
}

export interface MapKeyedListMutationOperationsArgs<TItem, TCreate, TUpdate> {
  /** The items the server holds, from the baseline. */
  previousItems: TItem[];
  /** The items the form holds now, in display order. */
  currentItems: TItem[];
  /** An item's server ID, or null when it has not been saved. */
  getId: (item: TItem) => string | null;
  /** Builds the payload for an unsaved item. */
  toCreate: (item: TItem, sortOrder: number) => TCreate;
  /** Builds an update, or null when the item and its position are unchanged. */
  toUpdate: (args: {
    previous: TItem;
    next: TItem;
    sortOrder: number;
  }) => TUpdate | null;
}

/**
 * Maps baseline and current items into mutation operations. Current position is
 * the sort order; baseline IDs absent from the current items are removed.
 */
export function mapKeyedListMutationOperations<TItem, TCreate, TUpdate>({
  previousItems,
  currentItems,
  getId,
  toCreate,
  toUpdate,
}: MapKeyedListMutationOperationsArgs<
  TItem,
  TCreate,
  TUpdate
>): KeyedListMutationOperations<TCreate, TUpdate> {
  const previousItemsById = new Map<string, TItem>();
  previousItems.forEach((item) => {
    const id = getId(item);
    if (id) {
      previousItemsById.set(id, item);
    }
  });

  const creates: TCreate[] = [];
  const updates: TUpdate[] = [];
  const survivingIds = new Set<string>();

  currentItems.forEach((item, sortOrder) => {
    const id = getId(item);
    const previousItem = id ? previousItemsById.get(id) : undefined;

    // Never update an ID that the baseline does not contain.
    if (!previousItem || !id) {
      creates.push(toCreate(item, sortOrder));
      return;
    }

    survivingIds.add(id);
    const update = toUpdate({
      previous: previousItem,
      next: item,
      sortOrder,
    });
    if (update) {
      updates.push(update);
    }
  });

  const removeIDs = Array.from(previousItemsById.keys()).filter(
    (id) => !survivingIds.has(id)
  );

  return { creates, updates, removeIDs };
}

/** True when no mutation operation is required. */
export function isEmptyKeyedListMutationOperations<TCreate, TUpdate>(
  operations: KeyedListMutationOperations<TCreate, TUpdate>
): boolean {
  return (
    operations.creates.length === 0 &&
    operations.updates.length === 0 &&
    operations.removeIDs.length === 0
  );
}
